import {
    Box,
    Button,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    BarChart3,
    Car,
    Check,
    ChevronDown,
    ClipboardCheck,
    ClipboardList,
    CreditCard,
    Eye,
    FileText,
    History,
    Landmark,
    LayoutDashboard,
    LayoutGrid,
    ListChecks,
    Lock,
    Package,
    Search,
    Settings as SettingsIcon,
    ShieldAlert,
    ShieldCheck,
    ShoppingCart,
    Sparkles,
    Truck,
    UserCog,
    Users,
    Wallet,
    Wrench,
} from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { httpClient } from '../../common/httpclient';
import usePermissions from '../../hooks/usePermissions';
import { ACTIONS, AREAS, AUTO_GRANTS, DEPENDENTS, OTHER_AREA, REQUIRES, SECTION_BY_KEY } from './permissionCatalog';
import BooleanPill from '../../component/common/BooleanPill';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { MobileProfileForm } from '../../component/system/MobileSystemForms';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';

/**
 * Formulário de perfil — matriz de PERMISSÕES DE NEGÓCIO por área (modelo RBAC, ver
 * `fv-api/RBAC.md`). Ref: `prototipo-lista-perfis.html` (view-form), FRONTEND-TASKS.md §12.
 *
 * `GET /profiles/permissions` devolve as permissões com `Key` (<recurso>.<ação>), `ID`
 * (é o que se grava em `profile_permissions`), `DisplayName` e `Group` legados. As áreas, os
 * rótulos em português, as ações sensíveis e as dependências (marcar uma ação marca a
 * "listar" do recurso) vêm de `./permissionCatalog.js`. Permissão devolvida pela API sem
 * rótulo no catálogo aparece na área "Outras" com o `DisplayName`. Áreas de módulos do plano
 * não contratados ficam ocultas (as permissões já marcadas continuam no perfil e são salvas).
 * Os grupos `discounts`, `promotions` e `programloyalty` não existem mais no catálogo.
 */

// Ícones por área — os mesmos já usados em `navConfig.js` para a respectiva tela.
const AREA_ICONS = {
    home: LayoutDashboard,
    sales: ShoppingCart,
    serviceorders: ClipboardList,
    servicequotes: FileText,
    customers: Users,
    suppliers: Truck,
    vehicles: Car,
    checklist: ListChecks,
    products: Package,
    purchases: ShoppingCart,
    categories: LayoutGrid,
    inventory: ClipboardCheck,
    services: Wrench,
    cashregister: Wallet,
    cardmachines: CreditCard,
    accounts: Landmark,
    reports: BarChart3,
    users: UserCog,
    settings: SettingsIcon,
    audit: History,
    ai: Sparkles,
    other: ShieldCheck,
};

function moduleState(mod, checkedPerms) {
    const total = mod.perms.length;
    const checkedCount = mod.perms.filter((p) => checkedPerms.has(p.id)).length;
    if (checkedCount === 0) return 'none';
    if (checkedCount === total) return 'full';
    return 'partial';
}

/**
 * Um módulo da matriz (`acc-section`) — diferente do `AccordionSection` dos
 * demais wizards (`users/Form.js`/`suppliers/Form.js`): aqui o cabeçalho tem
 * um switch mestre "selecionar tudo" (com um 3º estado visual "parcial",
 * já que `Switch` do MUI não tem `indeterminate` nativo) + badge "N de M"
 * em vez de um badge de conclusão, e várias seções podem ficar abertas ao
 * mesmo tempo (controlado de fora, via `openModules`) — ver
 * DESIGN-SYSTEM.md §5.21.
 */
function ModuleAccordionSection({
    mod, open, locked, checkedPerms, permsToShow, onToggleHeader, onToggleAll, onTogglePerm,
}) {
    const theme = useTheme();
    const Icon = mod.icon;
    const total = mod.perms.length;
    const checkedCount = mod.perms.filter((p) => checkedPerms.has(p.id)).length;
    const state = moduleState(mod, checkedPerms);

    return (
        <Box
            id={`profile-module-${mod.key}`}
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
                scrollMarginTop: '20px',
            }}
        >
            <Box
                onClick={onToggleHeader}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{mod.label}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{mod.description}</Typography>
                </Box>
                <Box
                    component="span"
                    sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        px: '9px',
                        py: '4px',
                        borderRadius: '20px',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        border: state === 'none' ? '1px solid' : 'none',
                        borderColor: 'divider',
                        bgcolor: state === 'full' ? 'success.bg' : state === 'partial' ? 'warning.bg' : 'background.subtle',
                        color: state === 'full' ? 'success.main' : state === 'partial' ? 'warning.main' : 'text.disabled',
                    }}
                >
                    {checkedCount} de {total}
                </Box>
                <Switch
                    checked={state === 'full'}
                    disabled={locked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onToggleAll(e.target.checked)}
                    title="Selecionar tudo neste módulo"
                    sx={state === 'partial' ? {
                        '& .MuiSwitch-switchBase': { transform: 'translateX(8px) !important' },
                        '& .MuiSwitch-track': {
                            backgroundImage: 'none !important',
                            backgroundColor: `${theme.palette.primary.main} !important`,
                            opacity: '0.4 !important',
                        },
                    } : undefined}
                />
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 2000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box
                    sx={{
                        p: '2px 20px 22px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: '10px',
                    }}
                >
                    {permsToShow.map((p, idx) => {
                        const checked = checkedPerms.has(p.id);
                        const showSection = p.section && (idx === 0 || permsToShow[idx - 1].section !== p.section);
                        return (
                            <Fragment key={p.id}>
                            {showSection && (
                                <Typography sx={{ gridColumn: '1 / -1', mt: idx === 0 ? 0 : 1, fontSize: '11.5px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.disabled' }}>
                                    {p.section}
                                </Typography>
                            )}
                            <Box
                                onClick={() => onTogglePerm(p.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    p: '11px 12px',
                                    borderRadius: '12px',
                                    border: '1.5px solid',
                                    borderColor: checked ? 'primary.main' : 'divider',
                                    backgroundColor: checked ? 'info.bg' : 'background.subtle',
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                    opacity: locked ? 0.85 : 1,
                                    transition: '.15s',
                                    '&:hover': locked ? undefined : { borderColor: 'primary.main' },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '6px',
                                        border: '1.5px solid',
                                        flexShrink: 0,
                                        borderColor: checked ? 'transparent' : 'divider',
                                        backgroundImage: checked ? theme.palette.gradient.brand : 'none',
                                        backgroundColor: checked ? undefined : 'background.paper',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {checked && <Check size={12} strokeWidth={3} />}
                                </Box>
                                <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary', flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                                    {p.label}
                                </Typography>
                                {p.sensitive && (
                                    <Box component="span" title="Ação sensível" sx={{ display: 'inline-flex', flexShrink: 0, color: 'warning.main' }}>
                                        <ShieldAlert size={14} />
                                    </Box>
                                )}
                                {locked && checked && (
                                    <Lock size={13} color={theme.palette.text.disabled} style={{ flexShrink: 0 }} />
                                )}
                            </Box>
                            </Fragment>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}

function PreviewModuleRow({ mod, checkedPerms }) {
    const state = moduleState(mod, checkedPerms);
    const checkedCount = mod.perms.filter((p) => checkedPerms.has(p.id)).length;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                py: '7px',
                fontSize: 12,
                color: 'text.secondary',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: state === 'full' ? 'success.main' : state === 'partial' ? 'warning.main' : 'divider',
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {mod.label}
            </Box>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.disabled', flexShrink: 0 }}>
                {checkedCount}/{mod.perms.length}
            </Box>
        </Box>
    );
}

export default function Form() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const [loadingCatalog, setLoadingCatalog] = useState(true);
    const [loadingProfile, setLoadingProfile] = useState(!!id);
    const [saving, setSaving] = useState(false);

    const { hasModule, loading: loadingPlan } = usePermissions();
    const [allModules, setModules] = useState([]);
    // Só mostra áreas de módulos contratados no plano (aplicado depois que o /me carrega).
    // As permissões já marcadas de áreas ocultas continuam em `checkedPerms` e são salvas.
    const modules = allModules.filter((mod) => !mod.module || loadingPlan || hasModule(mod.module));
    const [name, setName] = useState('');
    // Só passa a `true` depois de carregar um perfil de Sistema existente —
    // nunca é escolhível na criação (mesma regra do protótipo: o tipo
    // "Sistema" é uma trava fixa, não uma opção do formulário).
    const [isAdmin, setIsAdmin] = useState(false);
    // Flag da criação: marca o novo perfil como administrador e concede todas as permissões.
    const [adminFlag, setAdminFlag] = useState(false);
    const [checkedPerms, setCheckedPerms] = useState(new Set());
    const [openModules, setOpenModules] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    useAppBarTitle(isMobileShell ? (id ? `Perfil: ${name || '…'}` : 'Novo perfil') : '');

    const handleShowSuccessSnackbar = (text) => {
        setSeverity('success');
        setMessage(text);
        setOpenSnackbar(true);
    };
    const handleShowErrorSnackbar = (responseData) => {
        let text = 'Erro desconhecido';
        if (typeof responseData === 'string' && responseData.trim()) {
            text = responseData;
        } else if (responseData && typeof responseData === 'object') {
            text = responseData.message || responseData.error || JSON.stringify(responseData);
        }
        setSeverity('error');
        setMessage(text);
        setOpenSnackbar(true);
    };

    // Catálogo de permissões de negócio (`GET /profiles/permissions`): a resposta vem agrupada
    // por grupo legado; aqui é achatada e reagrupada por ÁREA (`permissionCatalog.js`) pela `Key`.
    useEffect(() => {
        const fetchCatalog = async () => {
            setLoadingCatalog(true);
            try {
                const response = await httpClient.get('/profiles/permissions');
                const data = response.data && typeof response.data === 'object' ? response.data : {};
                const flat = Object.values(data)
                    .flatMap((list) => (Array.isArray(list) ? list : []))
                    .filter((p) => p && p.ID && p.Key);
                const byKey = new Map(flat.map((p) => [p.Key, p]));
                const toPerm = (p) => ({
                    id: p.ID,
                    key: p.Key,
                    label: (ACTIONS[p.Key] && ACTIONS[p.Key].label) || p.DisplayName || p.Key,
                    sensitive: !!(ACTIONS[p.Key] && ACTIONS[p.Key].s),
                    section: SECTION_BY_KEY[p.Key] || null,
                });
                const known = new Set(AREAS.flatMap((area) => area.keys));
                const areas = [
                    ...AREAS.map((area) => ({ ...area, perms: area.keys.filter((k) => byKey.has(k)).map((k) => toPerm(byKey.get(k))) })),
                    { ...OTHER_AREA, perms: flat.filter((p) => !known.has(p.Key)).map(toPerm) },
                ];
                const built = areas
                    .map((area) => ({ ...area, icon: AREA_ICONS[area.key] || ShieldCheck }))
                    .filter((mod) => mod.perms.length > 0);
                setModules(built);
                setOpenModules(isMobileShell ? new Set() : new Set(built.map((mod) => mod.key)));
            } catch (error) {
                console.error('Error fetching permission catalog:', error);
                handleShowErrorSnackbar('Erro ao carregar catálogo de permissões');
                setModules([]);
            } finally {
                setLoadingCatalog(false);
            }
        };
        fetchCatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // `GET /profiles/{id}` (`fv-api` `user_profile.Getter.GetById`) devolve
    // `permissions` como array plano de ids (`[]string`, já resolvido a
    // partir de `profile_permissions`).
    useEffect(() => {
        if (!id) return;
        const fetchProfile = async () => {
            setLoadingProfile(true);
            try {
                const response = await httpClient.get(`/profiles/${id}`);
                const data = response.data || {};
                setName(data.name || '');
                setIsAdmin(!!data.is_admin);
                const perms = Array.isArray(data.permissions) ? data.permissions : [];
                setCheckedPerms(new Set(perms.map((p) => (typeof p === 'object' ? (p.id || p.ID) : p))));
            } catch (error) {
                console.error('Error fetching profile:', error);
                handleShowErrorSnackbar('Erro ao carregar perfil');
            } finally {
                setLoadingProfile(false);
            }
        };
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const locked = isAdmin;
    const matrixLocked = locked || adminFlag;
    const totalPerms = modules.reduce((sum, mod) => sum + mod.perms.length, 0);
    const modulesWithAccess = modules.filter((mod) => moduleState(mod, checkedPerms) !== 'none').length;
    const coveragePct = modules.length ? Math.round((modulesWithAccess / modules.length) * 100) : 0;
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * coveragePct / 100);

    const term = searchTerm.trim().toLowerCase();
    const isSearching = term.length > 0;
    const modulesRender = modules
        .map((mod) => {
            const nameMatches = !term || mod.label.toLowerCase().includes(term);
            const matchingPerms = term ? mod.perms.filter((p) => p.label.toLowerCase().includes(term)) : mod.perms;
            const visible = !term || nameMatches || matchingPerms.length > 0;
            const permsToShow = (term && !nameMatches) ? matchingPerms : mod.perms;
            const isOpen = openModules.has(mod.key) || (isSearching && (nameMatches || matchingPerms.length > 0));
            return { mod, visible, permsToShow, isOpen };
        })
        .filter((entry) => entry.visible);

    const toggleModuleAcc = (key) => {
        setOpenModules((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };
    const openModuleSection = (key) => {
        setOpenModules((prev) => new Set(prev).add(key));
        requestAnimationFrame(() => {
            const el = document.getElementById(`profile-module-${key}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };
    const setAllAcc = (open) => {
        setOpenModules(open ? new Set(modules.map((mod) => mod.key)) : new Set());
    };
    const toggleAdminFlag = (checked) => {
        setAdminFlag(checked);
        setCheckedPerms(checked ? new Set(allModules.flatMap((mod) => mod.perms.map((p) => p.id))) : new Set());
    };
    const toggleModuleAll = (mod, checked) => {
        if (matrixLocked) return;
        setCheckedPerms((prev) => {
            const next = new Set(prev);
            mod.perms.forEach((p) => { if (checked) next.add(p.id); else next.delete(p.id); });
            return next;
        });
    };
    // Dependências (permissionCatalog.js): marcar uma ação marca também "listar" do recurso;
    // desmarcar "listar" desmarca as ações que dependem dela.
    const idByKey = new Map(allModules.flatMap((mod) => mod.perms.map((p) => [p.key, p.id])));
    const togglePerm = (permId) => {
        if (matrixLocked) return;
        const key = allModules.flatMap((mod) => mod.perms).find((p) => p.id === permId)?.key;
        setCheckedPerms((prev) => {
            const next = new Set(prev);
            if (next.has(permId)) {
                next.delete(permId);
                (DEPENDENTS[key] || []).forEach((dep) => { if (idByKey.has(dep)) next.delete(idByKey.get(dep)); });
            } else {
                next.add(permId);
                const required = REQUIRES[key];
                if (required && idByKey.has(required)) next.add(idByKey.get(required));
                (AUTO_GRANTS[key] || []).forEach((auto) => { if (idByKey.has(auto)) next.add(idByKey.get(auto)); });
            }
            return next;
        });
    };

    const goBack = () => navigate('/profiles');

    const handleSave = async () => {
        if (locked) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            handleShowErrorSnackbar('Informe o nome do perfil.');
            return;
        }
        if (checkedPerms.size === 0) {
            handleShowErrorSnackbar('Selecione ao menos uma permissão para o perfil.');
            return;
        }

        const payload = {
            name: trimmedName,
            is_admin: isAdmin || adminFlag,
            permissions: Array.from(checkedPerms),
        };

        setSaving(true);
        try {
            if (id) {
                const response = await httpClient.put(`/profiles/${id}`, payload);
                if (response.status === 204) {
                    handleShowSuccessSnackbar('Perfil atualizado com sucesso!');
                } else {
                    handleShowErrorSnackbar(response.data);
                }
            } else {
                const response = await httpClient.post('/profiles', payload);
                if (response.status === 201) {
                    setName('');
                    setAdminFlag(false);
                    setCheckedPerms(new Set());
                    handleShowSuccessSnackbar('Perfil criado com sucesso!');
                } else {
                    handleShowErrorSnackbar(response.data);
                }
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao salvar perfil');
        } finally {
            setSaving(false);
        }
    };

    const requiredFieldLabel = (text) => (
        <>
            {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </>
    );

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
    };

    const cardSx = {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${theme.customRadius.card}px`,
        boxShadow: theme.customShadows.sm,
        backgroundColor: 'background.paper',
        p: '18px',
    };

    const isLoadingData = loadingCatalog || loadingProfile;

    // Shell mobile: formulário do protótipo `sistema/perfil-form.html` (mesma matriz, regras e envio).
    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget={id ? 'Editar' : 'Novo'}
                links={[{ title: 'Perfis', link: '/profiles' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                {isLoadingData ? (
                    <Stack spacing={1.25}>
                        {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={64} sx={{ borderRadius: '16px' }} />)}
                    </Stack>
                ) : (
                    <MobileProfileForm
                        name={name}
                        onNameChange={setName}
                        locked={locked}
                        isEdit={Boolean(id)}
                        adminFlag={adminFlag}
                        adminDisabled={loadingCatalog || modules.length === 0}
                        onToggleAdmin={toggleAdminFlag}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        modules={modulesRender}
                        onToggleModule={toggleModuleAcc}
                        isSearching={isSearching}
                        matrixLocked={matrixLocked}
                        checkedPerms={checkedPerms}
                        onToggleAll={toggleModuleAll}
                        onTogglePerm={togglePerm}
                        emptyText={modules.length === 0
                            ? 'Não foi possível carregar o catálogo de permissões.'
                            : `Nenhum módulo ou permissão encontrado para "${searchTerm}".`}
                        onCancel={goBack}
                        onSave={handleSave}
                        saving={saving}
                    />
                )}
            </PageScaffold>
        );
    }

    if (isLoadingData) {
        return (
            <PageScaffold
                titlePageTarget={id ? 'Editar' : 'Novo'}
                links={[{ title: 'Perfis', link: '/profiles' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={140} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={90} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                    </Stack>
                    <Skeleton variant="rounded" height={380} sx={{ borderRadius: '20px' }} />
                </Box>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            titlePageTarget={id ? 'Editar' : 'Novo'}
            links={[{ title: 'Perfis', link: '/profiles' }]}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            <Box>
                {/* form-hero — título + descrição + anel de progresso "Cobertura". */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', mb: 2.25 }}>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                            {id ? 'Editar perfil' : 'Novo perfil'}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                            {locked
                                ? `Visualizando as permissões do perfil de sistema "${name}".`
                                : 'Defina o nome e marque as permissões liberadas em cada módulo — o cartão ao lado resume o acesso em tempo real.'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27"
                                    cy="27"
                                    r={RING_R}
                                    fill="none"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                                {coveragePct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cobertura</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                {modulesWithAccess} de {modules.length} módulos com acesso
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* locked-banner — perfil de Sistema, mesma regra de trava de `prototipo-lista-perfis.html`. */}
                {locked && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            backgroundColor: 'warning.bg',
                            color: 'warning.main',
                            borderRadius: `${theme.customRadius.card}px`,
                            p: '14px 16px',
                            fontSize: '12.5px',
                            lineHeight: 1.5,
                            mb: 2.25,
                        }}
                    >
                        <Lock size={17} style={{ flexShrink: 0, marginTop: 1 }} />
                        <Box>
                            <Typography component="b" sx={{ display: 'block', fontSize: 13, mb: 0.25, color: 'inherit', fontWeight: 700 }}>
                                Perfil de sistema — somente leitura
                            </Typography>
                            Perfis padrão do sistema (como Administrador) já vêm com todas as permissões concedidas e não podem ser renomeados nem ter suas permissões alteradas por aqui.
                        </Box>
                    </Box>
                )}

                {/* form-columns — matriz + preview sticky. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                    <Stack spacing={1.75} sx={{ minWidth: 0 }}>
                        {/* id-card — Identificação (único campo obrigatório). */}
                        <Box sx={cardSx}>
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px', display: 'block' }}>
                                Identificação
                            </Typography>
                            <TextField
                                autoFocus={!id}
                                fullWidth
                                label={requiredFieldLabel('Nome do perfil')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={locked}
                                variant="outlined"
                                sx={standaloneFieldSx}
                            />
                            {!id && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 1.75 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>Perfil administrador</Typography>
                                        <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                                            Marca o perfil como administrador e concede todas as permissões.
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={adminFlag}
                                        disabled={loadingCatalog || modules.length === 0}
                                        onChange={(e) => toggleAdminFlag(e.target.checked)}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* matrix-toolbar — busca ao vivo + Expandir/Recolher tudo. */}
                        <Box sx={{ ...cardSx, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', p: '14px 16px' }}>
                            <Box sx={{ flex: '1 1 260px', minWidth: 220 }}>
                                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                    Buscar
                                </Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Buscar módulo ou permissão…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <Search size={14} strokeWidth={2} style={{ marginRight: 8, flexShrink: 0, color: theme.palette.text.disabled }} />,
                                        sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: '1 1 auto' }} />
                            <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
                                <Typography
                                    onClick={() => setAllAcc(true)}
                                    sx={{ fontSize: 12, fontWeight: 700, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    Expandir tudo
                                </Typography>
                                <Typography
                                    onClick={() => setAllAcc(false)}
                                    sx={{ fontSize: 12, fontWeight: 700, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    Recolher tudo
                                </Typography>
                            </Stack>
                        </Box>

                        {/* step-pills — navegação rápida entre módulos (não fecha as outras seções). */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {modules.map((mod, idx) => {
                                const state = moduleState(mod, checkedPerms);
                                const isOpen = openModules.has(mod.key);
                                const checkedCount = mod.perms.filter((p) => checkedPerms.has(p.id)).length;
                                return (
                                    <Box
                                        key={mod.key}
                                        onClick={() => openModuleSection(mod.key)}
                                        title={`${mod.label}: ${checkedCount} de ${mod.perms.length}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '7px',
                                            px: '14px',
                                            py: '8px',
                                            borderRadius: '24px',
                                            border: '1.5px solid',
                                            borderColor: isOpen ? 'primary.main' : 'divider',
                                            backgroundColor: isOpen ? 'info.bg' : 'background.paper',
                                            color: isOpen ? 'primary.main' : 'text.secondary',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 17,
                                                height: 17,
                                                borderRadius: '50%',
                                                border: '2px solid',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 9,
                                                flexShrink: 0,
                                                borderColor: state === 'full' ? 'success.main' : state === 'partial' ? 'warning.main' : (isOpen ? 'primary.main' : 'text.disabled'),
                                                backgroundColor: state === 'full' ? 'success.main' : state === 'partial' ? 'warning.main' : 'transparent',
                                                color: state === 'none' ? 'transparent' : '#fff',
                                            }}
                                        >
                                            {idx + 1}
                                        </Box>
                                        {mod.label}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* matriz — uma acc-section por módulo, várias abertas ao mesmo tempo. */}
                        <Stack spacing={1.75}>
                            {modulesRender.map(({ mod, permsToShow, isOpen }) => (
                                <ModuleAccordionSection
                                    key={mod.key}
                                    mod={mod}
                                    open={isOpen}
                                    locked={matrixLocked}
                                    checkedPerms={checkedPerms}
                                    permsToShow={permsToShow}
                                    onToggleHeader={() => toggleModuleAcc(mod.key)}
                                    onToggleAll={(checked) => toggleModuleAll(mod, checked)}
                                    onTogglePerm={(permId) => togglePerm(permId)}
                                />
                            ))}
                        </Stack>

                        {modulesRender.length === 0 && (
                            <Box
                                sx={{
                                    p: '40px 20px',
                                    textAlign: 'center',
                                    color: 'text.disabled',
                                    fontSize: 13,
                                    backgroundColor: 'background.paper',
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: `${theme.customRadius.card}px`,
                                }}
                            >
                                {modules.length === 0
                                    ? 'Não foi possível carregar o catálogo de permissões.'
                                    : `Nenhum módulo ou permissão encontrado para "${searchTerm}".`}
                            </Box>
                        )}
                    </Stack>

                    {/* preview sticky */}
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                        <Typography
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '10.5px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '.06em',
                                color: 'text.disabled',
                                mb: 1.25,
                            }}
                        >
                            <Eye size={13} /> Pré-visualização ao vivo
                        </Typography>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: `${theme.customRadius.hero}px`,
                                backgroundColor: 'background.paper',
                                boxShadow: theme.customShadows.md,
                                overflow: 'hidden',
                                mb: 1.75,
                            }}
                        >
                            <Box sx={{ height: 78, backgroundImage: theme.palette.gradient.brand, position: 'relative' }}>
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '22px',
                                        backgroundColor: 'background.paper',
                                        border: '4px solid',
                                        borderColor: 'background.paper',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        position: 'absolute',
                                        left: 20,
                                        bottom: -32,
                                        boxShadow: theme.customShadows.md,
                                    }}
                                >
                                    <ShieldCheck size={26} />
                                </Box>
                            </Box>
                            <Box sx={{ p: '42px 20px 18px' }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                                    {name.trim() || 'Nome do perfil'}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }}>
                                    {isAdmin ? 'Perfil de sistema' : adminFlag ? 'Perfil administrador' : 'Perfil personalizado'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                                    <BooleanPill value={isAdmin || adminFlag} trueLabel="Sistema" falseLabel="Personalizado" />
                                </Box>
                                <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
                                    <span>Permissões concedidas</span>
                                    <Box component="b" sx={{ color: 'text.primary' }}>{checkedPerms.size} de {totalPerms}</Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 280, overflowY: 'auto', mt: 0.5 }}>
                                    {modules.map((mod) => (
                                        <PreviewModuleRow key={mod.key} mod={mod} checkedPerms={checkedPerms} />
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios. O switch no cabeçalho de cada módulo marca/desmarca todas as permissões dele de uma vez.
                        </Typography>
                    </Box>
                </Box>

                {/* Espaçador para compensar o rodapé fixo abaixo. */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                {/* action-footer fixo — Cancelar/Salvar (Fechar quando travado). */}
                <Box
                    sx={{
                        position: 'fixed',
                        left: actionFooterLeft,
                        right: 0,
                        bottom: 0,
                        zIndex: theme.zIndex.appBar,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                        px: 3,
                        py: 1.75,
                        pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                        pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                        pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                        transition: 'left .2s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                        {locked ? 'Perfil de sistema — somente leitura, nada aqui pode ser alterado.' : 'Campos com * são obrigatórios'}
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={goBack}>
                            {locked ? 'Fechar' : 'Cancelar'}
                        </Button>
                        {!locked && (
                            <Button variant="contained" onClick={handleSave} disabled={saving}>
                                {saving ? 'Salvando...' : (id ? 'Salvar alterações' : 'Salvar perfil')}
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Box>
        </PageScaffold>
    );
}
